import { BoldChip, BoldChipColors } from 'components/BoldChip';
import { getTrans, TranslationKey } from 'modules/i18n';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { GeneralStatus, StatusType } from '../types';

interface Props {
  status: StatusType;
  statusMap: Partial<
    Record<StatusType, { color: BoldChipColors; label: TranslationKey | '' }>
  >;
}

/**
 * Resolves the human-readable status label, including the legacy NEW_WEEK_STATUSES
 * flag behaviour. Extracted so other surfaces (e.g. the week-overview hero pill) render
 * the SAME label without duplicating the flag logic.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useStatusLabel = (
  status: StatusType,
  statusMap: Props['statusMap']
): string => {
  const { label } = statusMap[status] || { color: 'warning', label: '' };
  const newStatusFlag = useFeatureToggle(FeatureFlag.NEW_WEEK_STATUSES);

  const isOpen = status === GeneralStatus.OPEN;
  const isClosed =
    status === GeneralStatus.CLOSED ||
    status === GeneralStatus.OPERATOR_REVIEWING_TALENT_DATA;

  let translatedLabel = label ? getTrans(label) : status;

  // temporary logic to replicate previous behaviour
  // yes, we didn't use translations :(
  if (isOpen && !newStatusFlag.isEnabled) {
    translatedLabel = 'Open';
  }
  if (isClosed) {
    translatedLabel = 'Closed';
  }

  return translatedLabel;
};

export const StatusBadge = ({ status, statusMap }: Props) => {
  const { color } = statusMap[status] || { color: 'warning', label: '' };
  const translatedLabel = useStatusLabel(status, statusMap);

  return <BoldChip variant="soft" label={translatedLabel} color={color} />;
};
